import { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { ContentSkeleton, DialogSurface, ListRow, StatusBadge } from '../../design-system';
import { useAsyncResource } from '../../shared/useAsyncResource';
import { featureReleaseAccessReason, type FeatureReleaseUser } from './model';
import type { FeatureReleasePort } from './port';
import { FeatureReleaseUserPicker } from './UserPicker';

function UserAccess({ port, user, onViewAs }: { port: FeatureReleasePort; user: FeatureReleaseUser; onViewAs?: (user: FeatureReleaseUser) => void }) {
  const resource = useAsyncResource(() => port.userAccess(user.personId), [port, user.personId], '사용자의 구성원 앱 공개 상태를 불러오지 못했습니다.');
  const multipleAccounts = user.activeIdentityCount > 1 || resource.data?.some((access) => access.reason === 'MULTIPLE_ACCOUNTS');
  const ineligible = resource.data?.some((access) => access.reason === 'INELIGIBLE');
  return <Stack spacing={1.5}>
    <Typography component="h3" variant="subtitle2">{user.displayName}의 구성원 앱</Typography>
    {multipleAccounts && <Alert severity="warning">연결된 로그인 계정이 여러 개입니다. 계정별 권한을 확인해 주세요.</Alert>}
    {resource.loading ? <ContentSkeleton variant="list" rows={4} label="사용자별 앱 확인 중" /> : resource.error ? <Alert severity="error" action={<Button onClick={resource.reload}>다시 시도</Button>}>{resource.error.message}</Alert> : <>
      {resource.data?.map((access) => <ListRow key={access.key} label={access.label} density="compact" inset="none" trailing={<StatusBadge label={featureReleaseAccessReason(access)} tone={access.available ? 'success' : 'neutral'} />} />)}
      {onViewAs && user.eligible && <Button variant="weak" disabled={multipleAccounts || ineligible} onClick={() => onViewAs(user)}>다른 사용자로 보기</Button>}
    </>}
  </Stack>;
}

export function FeatureReleasePreviewDialog({ port, onClose, onViewAs }: { port: FeatureReleasePort; onClose: () => void; onViewAs?: (user: FeatureReleaseUser) => void }) {
  const [user, setUser] = useState<FeatureReleaseUser>();
  return <DialogSurface open title="사용자별 확인" help="구성원 화면에서 사용할 수 있는 앱을 확인합니다. 관리자 화면 접근은 공개 범위와 별개로 기존 관리 권한을 따릅니다." onClose={onClose} closeLabel="사용자별 확인 닫기" size="standard">
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: user ? 'minmax(0, 1fr) minmax(0, 1.15fr)' : 'minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        <FeatureReleaseUserPicker port={port} selectedPersonIds={user ? [user.personId] : []} onSelect={setUser} preview />
        {user && <UserAccess key={user.personId} port={port} user={user} onViewAs={onViewAs} />}
      </Box>
    </Stack>
  </DialogSurface>;
}
